import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined"
import Skeleton from "@mui/material/Skeleton"

import {
  HeaderTableCell,
  NoWrapTableCell,
  NumberTableCell,
  SortableHeaderCell,
  StickyHeaderTableCell,
  TruncatedText,
} from "../../../common/components/tables/cells"
import { formatCurrency } from "../../../common/utils"
import { RatingEditor } from "../candidates/RatingStars"
import {
  ConciergeCallStatsHeaderCells,
  ConciergeCallStatsLoadingCells,
  ConciergeCallStatsRowCells,
} from "./ConciergeCallStatsTableColumns"
import { LeadTagsCell, LeadTagsLoadingCell } from "./LeadTagsCell"
import RoleChip, { AGENT_ROLES } from "./RoleChip"

/** Checkbox + Name + Tags + Rating + remaining data columns + In Candidates. */
export const LEAD_LIST_TABLE_COLUMN_COUNT = 17
export const LEAD_LIST_MLS_TABLE_COLUMN_COUNT = LEAD_LIST_TABLE_COLUMN_COUNT + 2

export function LeadListTableHeaderCells({
  sortColumn,
  sortDirection,
  onSortColumnChanged,
  stickyNameCellSx,
  includeUploadName = false,
  includeAllTimeProductionColumns = false,
}) {
  const volumeLabel = includeAllTimeProductionColumns ? "LTM Volume" : "Volume"
  const unitsLabel = includeAllTimeProductionColumns ? "LTM Units" : "Units"

  return (
    <>
      <SortableHeaderCell
        HeaderCellComponent={StickyHeaderTableCell}
        columnName="name"
        label="Name"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
        props={{ sx: stickyNameCellSx }}
      />
      <HeaderTableCell>Tags</HeaderTableCell>
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="rating"
        label="Rating"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="total_production"
        label={volumeLabel}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="total_transactions_count"
        label={unitsLabel}
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      {includeAllTimeProductionColumns ? (
        <>
          <SortableHeaderCell
            HeaderCellComponent={HeaderTableCell}
            columnName="all_time_production"
            label="All-Time Volume"
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onClick={onSortColumnChanged}
          />
          <SortableHeaderCell
            HeaderCellComponent={HeaderTableCell}
            columnName="all_time_transactions_count"
            label="All-Time Units"
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onClick={onSortColumnChanged}
          />
        </>
      ) : null}
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="years_in_business"
        label="Years"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="role"
        label="Role"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="office_name"
        label="Office"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      {includeUploadName ? (
        <SortableHeaderCell
          HeaderCellComponent={HeaderTableCell}
          columnName="upload_name"
          label="Upload"
          sortColumn={sortColumn}
          sortDirection={sortDirection}
          onClick={onSortColumnChanged}
        />
      ) : null}
      <ConciergeCallStatsHeaderCells
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onSortColumnChanged={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="address"
        label="Address"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="city"
        label="City"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="state"
        label="State"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="mls"
        label="MLS"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
      <SortableHeaderCell
        HeaderCellComponent={HeaderTableCell}
        columnName="imported"
        label="In Candidates"
        sortColumn={sortColumn}
        sortDirection={sortDirection}
        onClick={onSortColumnChanged}
      />
    </>
  )
}

export function LeadListTableDataCells({
  lead,
  includeUploadName = false,
  includeAllTimeProductionColumns = false,
  onRatingChanged,
}) {
  return (
    <>
      <LeadTagsCell lead={lead} />
      <NoWrapTableCell>
        <RatingEditor
          rating={lead.rating}
          setRating={onRatingChanged?.(lead)}
        />
      </NoWrapTableCell>
      <NumberTableCell>{formatCurrency(lead.total_production)}</NumberTableCell>
      <NumberTableCell>{lead.total_transactions_count}</NumberTableCell>
      {includeAllTimeProductionColumns ? (
        <>
          <NumberTableCell>
            {formatCurrency(lead.all_time_production)}
          </NumberTableCell>
          <NumberTableCell>{lead.all_time_transactions_count}</NumberTableCell>
        </>
      ) : null}
      <NumberTableCell>{lead.years_in_business}</NumberTableCell>
      <NoWrapTableCell>
        {lead.role ? <RoleChip label={AGENT_ROLES[lead.role]} /> : ""}
      </NoWrapTableCell>
      <NoWrapTableCell title={lead.office_name}>
        <TruncatedText maxWidth={260}>{lead.office_name}</TruncatedText>
      </NoWrapTableCell>
      {includeUploadName ? (
        <NoWrapTableCell title={lead.upload_name}>
          <TruncatedText maxWidth={180}>{lead.upload_name}</TruncatedText>
        </NoWrapTableCell>
      ) : null}
      <ConciergeCallStatsRowCells lead={lead} />
      <NoWrapTableCell title={lead.address}>
        <TruncatedText maxWidth={260}>{lead.address}</TruncatedText>
      </NoWrapTableCell>
      <NoWrapTableCell title={lead.city}>
        <TruncatedText maxWidth={160}>{lead.city}</TruncatedText>
      </NoWrapTableCell>
      <NoWrapTableCell>{lead.state}</NoWrapTableCell>
      <NoWrapTableCell title={lead.mls}>
        <TruncatedText maxWidth={180}>
          {lead.mls != null && lead.mls !== "" ? lead.mls : ""}
        </TruncatedText>
      </NoWrapTableCell>
      <NoWrapTableCell sx={{ textAlign: "center" }}>
        {lead.imported ? <CheckCircleOutlinedIcon variant="b600" /> : null}
      </NoWrapTableCell>
    </>
  )
}

export function LeadListTableLoadingCells({
  includeUploadName = false,
  includeAllTimeProductionColumns = false,
}) {
  return (
    <>
      <LeadTagsLoadingCell />
      <NoWrapTableCell>
        <Skeleton variant="rectangular" width={100} height={24} />
      </NoWrapTableCell>
      <NumberTableCell>
        <Skeleton variant="text" width={80} />
      </NumberTableCell>
      <NumberTableCell>
        <Skeleton variant="text" width={40} />
      </NumberTableCell>
      {includeAllTimeProductionColumns ? (
        <>
          <NumberTableCell>
            <Skeleton variant="text" width={80} />
          </NumberTableCell>
          <NumberTableCell>
            <Skeleton variant="text" width={40} />
          </NumberTableCell>
        </>
      ) : null}
      <NumberTableCell>
        <Skeleton variant="text" width={40} />
      </NumberTableCell>
      <NoWrapTableCell>
        <Skeleton variant="text" width={80} />
      </NoWrapTableCell>
      <NoWrapTableCell>
        <Skeleton variant="text" width={120} />
      </NoWrapTableCell>
      {includeUploadName ? (
        <NoWrapTableCell>
          <Skeleton variant="text" width={100} />
        </NoWrapTableCell>
      ) : null}
      <ConciergeCallStatsLoadingCells />
      <NoWrapTableCell>
        <Skeleton variant="text" width={120} />
      </NoWrapTableCell>
      <NoWrapTableCell>
        <Skeleton variant="text" width={70} />
      </NoWrapTableCell>
      <NoWrapTableCell>
        <Skeleton variant="text" width={40} />
      </NoWrapTableCell>
      <NoWrapTableCell>
        <Skeleton variant="text" width={100} />
      </NoWrapTableCell>
      <NoWrapTableCell sx={{ textAlign: "center" }}>
        <Skeleton
          variant="circular"
          width={24}
          height={24}
          sx={{ mx: "auto" }}
        />
      </NoWrapTableCell>
    </>
  )
}
